import { Fragment, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Modal } from "bootstrap";
import ReactDOM from "react-dom";
import { AutoCompleteSuggestionPanel } from "../../View/algolia/autoCompleteSuggestionPanel";
import { PAGES } from "../../routeConfig";
import { useAuth } from "../../Context/AuthContext";
import { useGuestClickGuard } from "../../Context/hook/useGuestClickGuard";
import { isPageVisible, onVisibilityChange } from "../../utils/pageVisibility";
export const SearchSuggestion = () => {
  const [SearchTxt, setSearchTxt] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { srctxt } = useParams();
  const inputRef = useRef(null);
  const suggestRef = useRef(null);
  const animationRef = useRef({
    curTextNum: 0,
    curPlaceholder: "",
    blinkCounter: 0,
    animationActive: false,
    animationFrameId: 0,
    timeoutId: 0,
    restartTimeoutId: 0,
  });
  const { isAuthenticated } = useAuth();
  const isGuest = isAuthenticated === false;
  const handleGuestClick = useGuestClickGuard();

  const texts = ["Search for something…", "Search products", "Clearance sales"];
  const defaultPlaceholder = "Type here…";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const searchStr = params.get("q");
    setSearchTxt(searchStr ?? "");
  }, [window.location.search, srctxt]);

  useEffect(() => {
    handleHide();
  }, [window.location.href]);

  const handleShow = () => {
    if (isGuest) return;
    setIsOpen(true);
  };

  const handleHide = () => setIsOpen(false);

  const handleSearch = (query) => {
    if (isGuest) return;
    if (query) navigate(PAGES.SEARCH + "?q=" + encodeURIComponent(query.trim()));
  };
  useEffect(() => {
    const input = inputRef.current;

    const animate = () => {
      const anim = animationRef.current;
      if (!anim.animationActive) return;

      let t = texts[anim.curTextNum];
      let delay = 500;

      if (anim.curPlaceholder == t + "|" && anim.blinkCounter == 3) {
        anim.blinkCounter = 0;
        anim.curTextNum = anim.curTextNum >= texts.length - 1 ? 0 : anim.curTextNum + 1;
        anim.curPlaceholder = "|";
        anim.timeoutId = setTimeout(switchText, 1000);
        return;
      }

      if (anim.curPlaceholder === t + "|" && anim.blinkCounter < 3) {
        anim.curPlaceholder = t;
        anim.blinkCounter++;
      } else if (anim.curPlaceholder === t && anim.blinkCounter < 3) {
        anim.curPlaceholder = t + "|";
      } else {
        anim.curPlaceholder = t.slice(0, anim.curPlaceholder.length + 1) + "|";
        delay = 150;
      }

      input.placeholder = anim.curPlaceholder;

      anim.timeoutId = setTimeout(() => {
        if (anim.animationActive) {
          anim.animationFrameId = requestAnimationFrame(animate);
        }
      }, delay);
    };

    const switchText = () => {
      const anim = animationRef.current;
      if (!anim.animationActive) return;
      input.classList.add("imitatefocus");
      anim.timeoutId = setTimeout(() => {
        input.classList.remove("imitatefocus");
        input.placeholder = anim.curTextNum === 0 ? defaultPlaceholder : anim.curPlaceholder;
        anim.timeoutId = setTimeout(() => {
          input.placeholder = anim.curPlaceholder;
          if (anim.animationActive) anim.animationFrameId = requestAnimationFrame(animate);
        }, 100);
      }, 100);
    };

    const startAnimation = () => {
      const anim = animationRef.current;
      // Idempotent: without this, two overlapping restarts leave two rAF loops
      // running forever, since animationFrameId only tracks the most recent one.
      if (anim.animationActive) return;
      anim.animationActive = true;
      anim.animationFrameId = requestAnimationFrame(animate);
    };

    const stopAnimation = () => {
      const anim = animationRef.current;
      anim.animationActive = false;
      cancelAnimationFrame(anim.animationFrameId);
      clearTimeout(anim.timeoutId);
      clearTimeout(anim.restartTimeoutId);
    };

    const handleBlur = (e) => {
      if (!e.target.value) {
        // Restart animation after 2 seconds if input is empty
        const anim = animationRef.current;
        clearTimeout(anim.restartTimeoutId);
        anim.restartTimeoutId = setTimeout(startAnimation, 2000);
      }
    };

    if (isPageVisible()) startAnimation();

    const unsubscribeVisibility = onVisibilityChange((visible) => {
      if (!visible) {
        stopAnimation();
      } else if (document.activeElement !== input && !input.value) {
        startAnimation();
      }
    });

    // Stop/start animation on focus/blur
    input.addEventListener("focus", stopAnimation);
    input.addEventListener("blur", handleBlur);

    const handleClickOutside = (event) => {
      if (
        suggestRef.current &&
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        !suggestRef.current.contains(event.target)
      ) {
        handleHide();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      unsubscribeVisibility();
      stopAnimation();
      input.removeEventListener("focus", stopAnimation);
      input.removeEventListener("blur", handleBlur);
    };
  }, []);

  return (
    <Fragment>
      <div className="header-search position-relative d-none d-lg-block" id="input">
        <input
          className="header-search-form form-control me-2"
          type="text"
          placeholder="Search here"
          aria-label="Search"
          value={SearchTxt}
          onChange={(e) => setSearchTxt(e.target.value)}
          ref={inputRef}
          readOnly={isGuest}
          onMouseDownCapture={handleGuestClick}
          onClick={handleShow}
          onFocus={handleShow}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              // Drop focus and close the panel BEFORE navigating. Without the blur the input keeps
              // focus through the route change, so the suggestion dropdown stays sitting over the
              // results page (and onFocus re-opens it on the next click).
              inputRef.current?.blur();
              handleHide();
              handleSearch(SearchTxt);
            } else if (e.key === "Escape") {
              handleHide();
            }
          }}
        />
        <span className="wes-search" />
        <div id="suggest" />
        <div
          className={"desktop-view search-suggestions bg-white search-outter-layer" + (isOpen ? " is-open" : "")}
          aria-hidden={!isOpen}
        >
          <div className="container-fluid p-0 border-radius-20" ref={suggestRef}>
            <AutoCompleteSuggestionPanel search={SearchTxt} viewResultClick={handleHide} />
          </div>
        </div>
      </div>
    </Fragment>
  );
};
